/** @format */

import { lazy, Suspense } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { AuthLayout } from "@/components/AuthLayout";

const Dashboard = lazy(() => import("@/pages/Dashboard"));
const TodaysVisits = lazy(() => import("@/pages/TodaysVisits"));
const Patients = lazy(() => import("@/pages/Patients"));
const PatientDetail = lazy(() => import("@/pages/PatientDetail"));
const PatientVisits = lazy(() => import("@/pages/PatientVisits"));
const AddVisit = lazy(() => import("@/pages/AddVisit"));
const EditVisit = lazy(() => import("@/pages/EditVisit"));
const AddOrlVisit = lazy(() => import("@/pages/AddOrlVisit"));
const EditOrlVisit = lazy(() => import("@/pages/EditOrlVisit"));
const Bookings = lazy(() => import("@/pages/Bookings"));
const Users = lazy(() => import("@/pages/Users"));
const Login = lazy(() => import("@/pages/Login"));
const Settings = lazy(() => import("@/pages/Settings"));
const Account = lazy(() => import("@/pages/Account"));

// Import breadcrumb loaders
import {
  patientBreadcrumbLoader,
  patientVisitsBreadcrumbLoader,
  visitDetailBreadcrumbLoader,
  orlVisitDetailBreadcrumbLoader,
} from "@/lib/loaders/breadcrumbLoaders";

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}

const router = createBrowserRouter([
  {
    element: <AuthLayout />,
    children: [
      {
        path: "/login",
        element: (
          <PublicRoute>
            <Login />
          </PublicRoute>
        ),
      },
      {
        path: "/dashboard",
        element: (
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        ),
      },
      {
        path: "/vizitat-sotme",
        element: (
          <ProtectedRoute>
            <TodaysVisits />
          </ProtectedRoute>
        ),
      },
      {
        path: "/pacientet",
        element: (
          <ProtectedRoute>
            <Patients />
          </ProtectedRoute>
        ),
      },
      {
        path: "/pacientet/:id",
        element: (
          <ProtectedRoute>
            <PatientDetail />
          </ProtectedRoute>
        ),
        loader: patientBreadcrumbLoader,
      },
      {
        path: "/pacientet/:id/visits",
        element: (
          <ProtectedRoute>
            <PatientVisits />
          </ProtectedRoute>
        ),
        loader: patientVisitsBreadcrumbLoader,
      },
      {
        path: "/pacientet/:id/visits/add",
        element: (
          <ProtectedRoute>
            <AddVisit />
          </ProtectedRoute>
        ),
        loader: patientBreadcrumbLoader,
      },
      {
        path: "/pacientet/:id/orl-visits/add",
        element: (
          <ProtectedRoute>
            <AddOrlVisit />
          </ProtectedRoute>
        ),
        loader: patientBreadcrumbLoader,
      },
      {
        path: "/pacientet/:id/visits/:visitId/edit",
        element: (
          <ProtectedRoute>
            <EditVisit />
          </ProtectedRoute>
        ),
        loader: visitDetailBreadcrumbLoader,
      },
      {
        path: "/pacientet/:id/orl-visits/:visitId/edit",
        element: (
          <ProtectedRoute>
            <EditOrlVisit />
          </ProtectedRoute>
        ),
        loader: orlVisitDetailBreadcrumbLoader,
      },
      {
        path: "/rezervimet",
        element: (
          <ProtectedRoute>
            <Bookings />
          </ProtectedRoute>
        ),
      },
      {
        path: "/perdoruesit",
        element: (
          <ProtectedRoute>
            <Users />
          </ProtectedRoute>
        ),
      },
      {
        path: "/cilesimet",
        element: (
          <ProtectedRoute>
            <Settings />
          </ProtectedRoute>
        ),
      },
      {
        path: "/llogaria",
        element: (
          <ProtectedRoute>
            <Account />
          </ProtectedRoute>
        ),
      },
      {
        path: "/",
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: "*",
        element: <Navigate to="/dashboard" replace />,
      },
    ],
  },
]);

export function Router() {
  return (
    <Suspense fallback={null}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
